import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { Link as RouterLink } from 'react-router-dom';
import DatePickerField from '../components/common/DatePickerField';
import { LoadingState, useErrorToast } from '../components/common';
import {
  normalizeHttpUrl,
  toSafeCssImageUrl,
  toSafeHttpUrl,
  toSafeImageUrl,
} from '../components/common/safeUrl';
import {
  EVENT_STATUS_META,
  RECORD_ROOM_META,
  getEventStatus,
  isRecordRoom,
} from '../components/event/eventStatus';
import { EVENT_TYPE_META, type EventType } from '../components/event/eventType';
import { useEventContext } from '../contexts/EventContext';
import { EVENT_LIST_ROUTE_PATH } from '../routes/appRoutes';
import type { ManagerRole } from '../api/managerApi';
import {
  eventService,
  type EventSummary,
  type EventUpdateRequest,
} from '../services/eventService';
import { loadMyManagerProfile } from '../services/managerService';
import { fetchHasNeedsReviewResult } from '../services/resultApi';

type InfoRowProps = Readonly<{
  label: string;
  children: React.ReactNode;
}>;

function InfoRow({ label, children }: InfoRowProps) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 44, py: 1 }}>
      <Box sx={{ width: 120, flexShrink: 0, color: '#8a94a6', fontSize: 13, fontWeight: 700 }}>
        {label}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, color: '#111827', fontSize: 14 }}>{children}</Box>
    </Box>
  );
}

// 편집 모드에서 쓰는 한 줄 입력 필드
type EditFieldProps = Readonly<{
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
}>;

function EditField({ value, onChange, onBlur, placeholder }: EditFieldProps) {
  return (
    <TextField
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      placeholder={placeholder}
      size="small"
      fullWidth
      variant="outlined"
      sx={{ '& .MuiInputBase-input': { fontSize: 14, py: 0.75 } }}
    />
  );
}

type RegisterUrlViewProps = Readonly<{
  registerUrl: string;
}>;

function RegisterUrlView({ registerUrl }: RegisterUrlViewProps) {
  const normalizedRegisterUrl = normalizeHttpUrl(registerUrl);
  const safeRegisterUrl = toSafeHttpUrl(normalizedRegisterUrl);

  if (!safeRegisterUrl) {
    return <Box sx={{ color: '#cbd5e1' }}>-</Box>;
  }

  return (
    <Link
      href={safeRegisterUrl}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        color: '#1da1f2',
        display: 'inline-block',
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {normalizedRegisterUrl}
    </Link>
  );
}

type RegisterUrlFieldProps = Readonly<{
  isEditing: boolean;
  draft: EventSummary | null;
  registerUrl: string;
  onDraftChange: <K extends keyof EventSummary>(key: K, value: EventSummary[K]) => void;
}>;

function RegisterUrlField({
  isEditing,
  draft,
  registerUrl,
  onDraftChange,
}: RegisterUrlFieldProps) {
  if (!isEditing || !draft) {
    return <RegisterUrlView registerUrl={registerUrl} />;
  }

  return (
    <EditField
      value={draft.registerUrl}
      onChange={(value) => onDraftChange('registerUrl', value)}
      onBlur={() => onDraftChange('registerUrl', normalizeHttpUrl(draft.registerUrl))}
      placeholder="https://"
    />
  );
}

type EventPosterProps = Readonly<{
  isEditing: boolean;
  posterSrc: string;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onImageChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}>;

function EventPoster({ isEditing, posterSrc, fileInputRef, onImageChange }: EventPosterProps) {
  const safeBackgroundImage = toSafeCssImageUrl(posterSrc, {
    allowBlobImage: true,
    allowDataImage: true,
  });
  const hasPoster = safeBackgroundImage !== 'none';

  const handleClick = () => {
    if (isEditing) {
      fileInputRef.current?.click();
    }
  };

  return (
    <Box
      onClick={handleClick}
      sx={{
        width: 280,
        aspectRatio: '3 / 4',
        flexShrink: 0,
        borderRadius: 2,
        border: '1px solid #e8edf3',
        backgroundColor: '#f8fafc',
        backgroundImage: safeBackgroundImage,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#cbd5e1',
        fontSize: 13,
        fontWeight: 600,
        cursor: isEditing ? 'pointer' : 'default',
        position: 'relative',
      }}
    >
      {!hasPoster && '포스터 이미지'}
      {isEditing && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.35)',
            color: '#fff',
            fontSize: 13,
            fontWeight: 700,
            opacity: 0,
            transition: 'opacity 0.15s',
            '&:hover': { opacity: 1 },
          }}
        >
          이미지 변경
        </Box>
      )}
      <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={onImageChange} />
    </Box>
  );
}

function getEventView(
  isEditing: boolean,
  draft: EventSummary | null,
  event: EventSummary | null,
) {
  if (isEditing) {
    return draft;
  }

  return event;
}

function getPosterSrc(isEditing: boolean, imagePreview: string | null, imageUrl: string) {
  if (isEditing && imagePreview) {
    return toSafeImageUrl(imagePreview, { allowBlobImage: true, allowDataImage: true });
  }

  return toSafeImageUrl(imageUrl);
}

function updateDraftValue<K extends keyof EventSummary>(
  prev: EventSummary | null,
  key: K,
  value: EventSummary[K],
) {
  if (!prev) {
    return prev;
  }

  return { ...prev, [key]: value };
}

function EventInfoPage() {
  const { selectedEventId, setSelectedEventName } = useEventContext();
  const [event, setEvent] = useState<EventSummary | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState<EventSummary | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [managerRole, setManagerRole] = useState<ManagerRole | null>(null);
  const [isEndNeedsReviewWarningOpen, setIsEndNeedsReviewWarningOpen] = useState(false);
  const [isCheckingNeedsReview, setIsCheckingNeedsReview] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showError, errorToast } = useErrorToast();
  const canManageEvents = managerRole === 'SUPER' || managerRole === 'MIDDLE';

  useEffect(() => {
    if (selectedEventId == null) return;
    eventService
      .detail(selectedEventId)
      .then((detail) => {
        setEvent(detail);
        // 헤더 대회명을 최신 값으로 동기화 (새로고침 직접 접속·이름 변경 대비)
        setSelectedEventName(detail.title);
      })
      .catch((error) => {
        showError(error);
      });
  }, [selectedEventId, setSelectedEventName, showError]);

  useEffect(() => {
    let ignore = false;

    loadMyManagerProfile()
      .then((profile) => {
        if (!ignore) {
          setManagerRole(profile.role);
        }
      })
      .catch(() => {
        if (!ignore) {
          setManagerRole(null);
        }
      });

    return () => {
      ignore = true;
    };
  }, []);

  const startEdit = () => {
    if (!canManageEvents) return;
    if (!event) return;
    setDraft(event);
    setImageFile(null);
    setImagePreview(null);
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setImageFile(null);
    setImagePreview(null);
  };

  const save = async () => {
    if (!canManageEvents) return;
    if (!draft || selectedEventId == null) return;
    setIsSaving(true);
    try {
      // isPublished/isEnd 는 즉시 저장 토글로 분리됨 — 여기선 보내지 않음
      const payload: EventUpdateRequest = {
        title: draft.title,
        city: draft.city,
        location: draft.location,
        hostName: draft.hostName,
        eventDate: draft.eventDate,
        type: draft.type,
        registrationStartDate: draft.registrationStartDate ?? undefined,
        registrationEndDate: draft.registrationEndDate ?? undefined,
        registerUrl: normalizeHttpUrl(draft.registerUrl),
      };
      const updated = await eventService.update(selectedEventId, payload, imageFile);
      setEvent(updated);
      cancelEdit();
    } catch (error) {
      showError(error);
    } finally {
      setIsSaving(false);
    }
  };

  const updateDraft = <K extends keyof EventSummary>(key: K, value: EventSummary[K]) =>
    setDraft((prev) => updateDraftValue(prev, key, value));

  // 발행/종료/수집 여부 토글 — 누르는 즉시 해당 필드만 저장 (낙관적 업데이트 + 실패 시 롤백)
  const toggleField = async (
    key: 'isPublished' | 'isEnd' | 'collectBirth' | 'collectGender',
    value: boolean,
  ) => {
    if (!canManageEvents) return;
    if (!event || selectedEventId == null) return;
    const prev = event;
    setEvent({ ...event, [key]: value });
    // 편집 중이면 draft 도 같이 맞춰 저장 시 덮어쓰지 않게 함
    setDraft((d) => updateDraftValue(d, key, value));
    try {
      const updated = await eventService.update(selectedEventId, { [key]: value });
      setEvent(updated);
    } catch (error) {
      setEvent(prev);
      setDraft((d) => updateDraftValue(d, key, prev[key]));
      showError(error);
    }
  };

  // 대회 종료 토글 활성화 시, 검토필요 상태 참가자가 있으면 확정 전 경고를 띄운다.
  const handleEndToggle = async (value: boolean) => {
    if (!value) {
      void toggleField('isEnd', false);
      return;
    }
    if (!event || selectedEventId == null) return;
    setIsCheckingNeedsReview(true);
    try {
      const hasNeedsReview = await fetchHasNeedsReviewResult(selectedEventId);
      if (hasNeedsReview) {
        setIsEndNeedsReviewWarningOpen(true);
        return;
      }
    } catch (error) {
      showError(error);
      return;
    } finally {
      setIsCheckingNeedsReview(false);
    }
    void toggleField('isEnd', true);
  };

  const confirmEndDespiteNeedsReview = () => {
    setIsEndNeedsReviewWarningOpen(false);
    void toggleField('isEnd', true);
  };

  const handlePickImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
  };

  // 편집 중에는 draft, 아니면 서버 데이터
  const view = getEventView(isEditing, draft, event);

  if (selectedEventId == null) {
    return (
      <section className="page-placeholder">
        <div className="page-content">
          <Box sx={{ color: '#8a94a6', fontSize: 14, py: 6 }}>
            선택된 대회가 없습니다.{' '}
            <Link
              component={RouterLink}
              to={EVENT_LIST_ROUTE_PATH}
              sx={{ color: '#1da1f2', fontWeight: 700 }}
            >
              대회 목록에서 선택
            </Link>
            해 주세요.
          </Box>
        </div>
        {errorToast}
      </section>
    );
  }

  if (!view) {
    return (
      <section className="page-placeholder">
        <div className="page-content">
          <LoadingState message="대회 정보를 불러오는 중입니다." />
        </div>
        {errorToast}
      </section>
    );
  }

  const posterSrc = getPosterSrc(isEditing, imagePreview, view.imageUrl);

  return (
    <section className="page-placeholder">
      <div className="page-content">
        <Paper
          variant="outlined"
          sx={{ borderColor: '#e8edf3', borderRadius: 2, overflow: 'hidden' }}
        >
          {/* 헤더: 대회 코드 + 이름 + 상태 + 액션 버튼 */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 2,
              p: 3,
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Chip
                  label={view.eventCode}
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#1da1f2',
                    backgroundColor: '#e8f6fe',
                  }}
                />
                <Chip
                  label={EVENT_STATUS_META[getEventStatus(view)].label}
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: 11,
                    fontWeight: 700,
                    color: EVENT_STATUS_META[getEventStatus(view)].fg,
                    backgroundColor: EVENT_STATUS_META[getEventStatus(view)].bg,
                  }}
                />
                {isRecordRoom(view) && (
                  <Chip
                    label={RECORD_ROOM_META.label}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: 11,
                      fontWeight: 700,
                      color: RECORD_ROOM_META.fg,
                      backgroundColor: RECORD_ROOM_META.bg,
                    }}
                  />
                )}
              </Box>
              {isEditing && draft ? (
                <TextField
                  value={draft.title}
                  onChange={(e) => updateDraft('title', e.target.value)}
                  placeholder="대회 이름"
                  size="small"
                  variant="outlined"
                  sx={{
                    maxWidth: 420,
                    '& .MuiInputBase-input': { fontSize: 20, fontWeight: 800 },
                  }}
                />
              ) : (
                <Box sx={{ fontSize: 22, fontWeight: 800, color: '#111827' }}>{view.title}</Box>
              )}
            </Box>

            {canManageEvents && (
              <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
              {isEditing ? (
                <>
                  <Button
                    variant="text"
                    size="small"
                    onClick={cancelEdit}
                    disabled={isSaving}
                    sx={{ color: '#8a94a6', fontWeight: 700 }}
                  >
                    취소
                  </Button>
                  <Button
                    variant="contained"
                    disableElevation
                    size="small"
                    onClick={() => {
                      void save();
                    }}
                    disabled={isSaving}
                    sx={{
                      backgroundColor: '#1da1f2',
                      fontWeight: 700,
                      '&:hover': { backgroundColor: '#1791db' },
                    }}
                  >
                    저장
                  </Button>
                </>
              ) : (
                <Button
                  variant="contained"
                  disableElevation
                  size="small"
                  onClick={startEdit}
                  sx={{
                    backgroundColor: '#1da1f2',
                    fontWeight: 700,
                    '&:hover': { backgroundColor: '#1791db' },
                  }}
                >
                  수정하기
                </Button>
              )}
              </Box>
            )}
          </Box>

          <Divider sx={{ borderColor: '#eef2f7' }} />

          {/* 본문: 포스터 + 정보 */}
          <Box sx={{ display: 'flex', gap: 4, p: 3, flexWrap: 'wrap' }}>
            {/* 포스터 — 편집 모드에서 클릭 시 이미지 선택 */}
            <EventPoster
              isEditing={isEditing}
              posterSrc={posterSrc}
              fileInputRef={fileInputRef}
              onImageChange={handlePickImage}
            />

            {/* 정보 */}
            <Box sx={{ flex: 1, minWidth: 280 }}>
              <InfoRow label="분류">
                {isEditing && draft ? (
                  <Select
                    value={draft.type}
                    onChange={(e) => updateDraft('type', e.target.value as EventType)}
                    size="small"
                    fullWidth
                    sx={{ fontSize: 14, '& .MuiSelect-select': { py: 0.75 } }}
                  >
                    {(Object.keys(EVENT_TYPE_META) as EventType[]).map((type) => (
                      <MenuItem key={type} value={type} sx={{ fontSize: 14 }}>
                        {EVENT_TYPE_META[type].label}
                      </MenuItem>
                    ))}
                  </Select>
                ) : (
                  EVENT_TYPE_META[view.type].label
                )}
              </InfoRow>
              <InfoRow label="대회 도시">
                {isEditing && draft ? (
                  <EditField value={draft.city} onChange={(v) => updateDraft('city', v)} />
                ) : (
                  view.city
                )}
              </InfoRow>
              <InfoRow label="대회 장소">
                {isEditing && draft ? (
                  <EditField value={draft.location} onChange={(v) => updateDraft('location', v)} />
                ) : (
                  view.location
                )}
              </InfoRow>
              <InfoRow label="주최사">
                {isEditing && draft ? (
                  <EditField value={draft.hostName} onChange={(v) => updateDraft('hostName', v)} />
                ) : (
                  view.hostName
                )}
              </InfoRow>
              <InfoRow label="대회 일시">
                {isEditing && draft ? (
                  <DatePickerField
                    value={draft.eventDate}
                    onChange={(v) => updateDraft('eventDate', v)}
                  />
                ) : (
                  view.eventDate
                )}
              </InfoRow>
              <InfoRow label="접수 시작일">
                {isEditing && draft ? (
                  <DatePickerField
                    value={draft.registrationStartDate ?? ''}
                    onChange={(v) => updateDraft('registrationStartDate', v)}
                  />
                ) : (
                  view.registrationStartDate ?? '미정'
                )}
              </InfoRow>
              <InfoRow label="접수 마감일">
                {isEditing && draft ? (
                  <DatePickerField
                    value={draft.registrationEndDate ?? ''}
                    onChange={(v) => updateDraft('registrationEndDate', v)}
                  />
                ) : (
                  view.registrationEndDate ?? '미정'
                )}
              </InfoRow>
              <InfoRow label="참가 등록">
                <RegisterUrlField
                  isEditing={isEditing}
                  draft={draft}
                  registerUrl={view.registerUrl}
                  onDraftChange={updateDraft}
                />
              </InfoRow>

              <Divider sx={{ my: 1.5, borderColor: '#eef2f7' }} />

              <InfoRow label="홈페이지 노출">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Tooltip title="활성화 시 홈페이지에서도 확인하실 수 있습니다.">
                    <Box component="span" sx={{ display: 'inline-flex' }}>
                      <Switch
                        checked={!!event?.isPublished}
                        disabled={!canManageEvents}
                        onChange={(e) => {
                          void toggleField('isPublished', e.target.checked);
                        }}
                        size="small"
                      />
                    </Box>
                  </Tooltip>
                  <Box sx={{ fontSize: 13, color: '#8a94a6' }}>
                    {event?.isPublished ? '노출 중' : '비노출'}
                  </Box>
                </Box>
              </InfoRow>
              <InfoRow label="생년월일 수집">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Tooltip title="수집 시 참가자 등록에서 생년월일이 필수가 되고 연령대가 계산됩니다. 수집하지 않으면 입력값을 저장하지 않으며, 이미 저장된 값은 유지됩니다.">
                    <Box component="span" sx={{ display: 'inline-flex' }}>
                      <Switch
                        checked={!!event?.collectBirth}
                        disabled={!canManageEvents}
                        onChange={(e) => {
                          void toggleField('collectBirth', e.target.checked);
                        }}
                        size="small"
                      />
                    </Box>
                  </Tooltip>
                  <Box sx={{ fontSize: 13, color: '#8a94a6' }}>
                    {event?.collectBirth ? '수집(필수)' : '수집 안 함'}
                  </Box>
                </Box>
              </InfoRow>
              <InfoRow label="성별 수집">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Tooltip title="수집 시 참가자 등록에서 성별이 필수가 됩니다. 수집하지 않으면 입력값을 저장하지 않으며, 이미 저장된 값은 유지됩니다.">
                    <Box component="span" sx={{ display: 'inline-flex' }}>
                      <Switch
                        checked={!!event?.collectGender}
                        disabled={!canManageEvents}
                        onChange={(e) => {
                          void toggleField('collectGender', e.target.checked);
                        }}
                        size="small"
                      />
                    </Box>
                  </Tooltip>
                  <Box sx={{ fontSize: 13, color: '#8a94a6' }}>
                    {event?.collectGender ? '수집(필수)' : '수집 안 함'}
                  </Box>
                </Box>
              </InfoRow>
              <InfoRow label="기록실로 이동">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Tooltip title="홈페이지 노출이 활성화된 대회는 이 설정을 켜면 대회 기록실에, 끄면 접수 및 개최 예정 대회에 표시됩니다.">
                    <Box component="span" sx={{ display: 'inline-flex' }}>
                      <Switch
                        checked={!!event?.isEnd}
                        disabled={!canManageEvents || isCheckingNeedsReview}
                        onChange={(e) => {
                          void handleEndToggle(e.target.checked);
                        }}
                        size="small"
                      />
                    </Box>
                  </Tooltip>
                  <Box sx={{ fontSize: 13, color: '#8a94a6' }}>
                    {isCheckingNeedsReview
                      ? '검토필요 상태 확인 중…'
                      : event?.isEnd
                        ? '대회 기록실'
                        : '접수 및 개최 예정 대회'}
                  </Box>
                </Box>
              </InfoRow>
            </Box>
          </Box>
        </Paper>
      </div>

      <Dialog
        open={isEndNeedsReviewWarningOpen}
        onClose={() => setIsEndNeedsReviewWarningOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700, color: '#b45309' }}>
          검토필요 참가자가 있습니다
        </DialogTitle>
        <DialogContent>
          <Box sx={{ fontSize: 14, color: '#334155', lineHeight: 1.6 }}>
            완주 상태가 <b>검토필요</b>인 참가자가 있습니다.
            <br />
            결과를 확정하지 않고 기록실로 이동하면 참가자가 홈페이지에서 잘못되거나
            불완전한 기록을 볼 수 있습니다.
            <br />
            그래도 기록실로 이동하시겠습니까?
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsEndNeedsReviewWarningOpen(false)}>취소</Button>
          <Button color="warning" variant="contained" disableElevation onClick={confirmEndDespiteNeedsReview}>
            그래도 이동
          </Button>
        </DialogActions>
      </Dialog>

      {errorToast}
    </section>
  );
}

export default EventInfoPage;
